import  React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { TreeNode } from "./TreeNode";
import { Loader2 } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import customFetch from "../apicall/api";

import styles from "./TreeModal.module.css";

export const TreeModal = ({
  open,
  onOpenChange,
  // optional props used for the POST request below
  selectedKey = null, // corresponds to txn_ref_no
  objectId,
  viewName,
  forwardTracking: defaultForwardTracking = false,  // default to backward tracking
  title = "Data Tree View",
  description = "Click on nodes to explore their relationships",
}) => {
  const [rootNodes, setRootNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isForwardTracking, setIsForwardTracking] = useState(defaultForwardTracking);

  // const testApiEndpoint = "http://localhost:5000/history/transaction";

  useEffect(() => {
    if (open) {
      const controller = new AbortController();
      loadRootNodes(controller.signal);
      return () => controller.abort();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const controller = new AbortController();
      loadRootNodes(controller.signal);
      return () => controller.abort();
    }
  }, [isForwardTracking]);


  const loadRootNodes = async (signal) => {
    if (!selectedKey) return;
    setIsLoading(true);
    try {
      const payload = {
        selected_key: selectedKey,
        object_id: objectId,
        forward_tracking: isForwardTracking, // true when checked, false when unchecked
        top_level_flag: true,
        view_name:viewName,
      };
      console.log('API Payload forward_tracking value:', isForwardTracking);
      console.log('Sending POST request with payload:', payload);

      // const response = await fetch(testApiEndpoint, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json", "api-token": localStorage.getItem("api_token") },
      //   body: JSON.stringify(payload),
      //   signal,
      // });

      const response = await customFetch(
        process.env.REACT_APP_URL + "/history/transaction",
        {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            "api-token": localStorage.getItem("api_token"),
          },
          signal,
        }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Failed to fetch data: ${response.status} ${text}`);
      }

      const data = await response.json();
      // Check API response status
      if (data.status === 1 && data.levels && data.levels.length > 0) {
          // Transform levels data into nodes format
          const nodes = data.levels.map(level => {
            // 1. Destructure the nested metadata object for cleaner access
            const { metadata } = level;
            // 2. Dynamically create the metadata object for display
            const dynamicMetadata = Object.keys(metadata).reduce((acc, key) => {
              // Converts 'snake_case' keys (e.g., 'total_amount_collected') 
              // into 'Title Case' display keys (e.g., 'Total Amount Collected')
              const displayKey = key.split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
              // Assign the value from the metadata object
              acc[displayKey] = metadata[key];
              return acc;
            }, {}); // Initialize the accumulator as an empty object
              
            return {
            // NOTE: The keys below are adjusted based on your sample JSON 
            // where core fields like label and previous_transaction_id moved.

            // Use consistent ID for both id and objectId based on tracking direction
            // Assuming aggregation_id replaces _id from the old structure
            id: isForwardTracking ? level._id : level.previous_transaction_id,
            
            // Use the 'label' from the JSON object
            // label: level.label, 
            label1: (level.labels && level.labels[0]) || null,
            
            objectId: isForwardTracking ? level._id : level.previous_transaction_id,
            
            // Get amount from the nested metadata object
            // amount: metadata.total_amount, 
            label2: (level.labels && level.labels[1]) || null,
            
            // Check children based on the available ID fields
            hasChildren: isForwardTracking ? !!level._id : !!level.previous_transaction_id,
            
            // Use the dynamically generated metadata object
            metadata: dynamicMetadata 
            };
          });
        // // Transform levels data into nodes format
        // const nodes = data.levels.map(level => ({
        //   // Use consistent ID for both id and objectId based on tracking direction
        //   id: isForwardTracking ? level._id : level.previous_transaction_id,
        //   label: level.txn_ref_no_bank ? level.txn_ref_no_bank : level.txn_ref_no,
        //   objectId: isForwardTracking ? level._id : level.previous_transaction_id,
        //   amount: level.total_amount_collected,
        //   // For forward tracking, always show expand if _id exists, for backward use previous_transaction_id
        //   hasChildren: isForwardTracking ? !!level._id : !!level.previous_transaction_id,
        //   metadata: {
        //     'Household': `${level.household_name} (${level.household_number})`,
        //     'Status': level.status,
        //     'Remark': level.remark
        //   }
        // }));
        setRootNodes(nodes);
      } else if (data.status === 0 && data.message === 'NA') {
        // Add an artificial node showing no further levels
        const endNode = [{
          id: 'end-node',
          label1: 'No further levels available',
          objectId: null,
          label2: '',
          hasChildren: false,
          metadata: {},
          isEndNode: true // special flag to style this node differently if needed
        }];
        setRootNodes(endNode);
      }else if (data.status === 0 && data.message != "NA"){
        // Add an artificial node showing no further levels
        const endNode = [{
          id: 'end-node',
          label1: data.message,
          objectId: null,
          label2: '',
          hasChildren: false,
          metadata: {},
          isEndNode: true // special flag to style this node differently if needed
        }];
        setRootNodes(endNode);
      }else {
        // If status is not 1, show the message as error
        toast.error(data.message || 'Failed to load data');
        setRootNodes([]);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error loading root nodes:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = async (nodeId) => {
    try {
      if (!selectedKey) return [];

      // Get the clicked node's previous_transaction_id (stored in objectId)
      const payload = {
        selected_key: selectedKey,
        object_id: nodeId,  // nodeId here is actually the previous_transaction_id from the parent
        forward_tracking: isForwardTracking,
        view_name:viewName,
      };

      // const resp = await fetch(testApiEndpoint, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" , "api-token": localStorage.getItem("api_token")},
      //   body: JSON.stringify(payload),
      // });

      const resp = await customFetch(
        process.env.REACT_APP_URL + "/history/transaction",
        {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            "api-token": localStorage.getItem("api_token"),
          }
        }
      );

      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`Failed to fetch children: ${resp.status} ${text}`);
      }

      const data = await resp.json();
      if (data.status === 1 && data.levels && data.levels.length > 0) {
            // Transform levels data into nodes format, same as root nodes
            return data.levels.map(level => {
              const { metadata } = level;

              // Function to dynamically create the metadata object for display
              const dynamicMetadata = Object.keys(metadata).reduce((acc, key) => {
                  // Converts 'snake_case' keys (e.g., 'total_amount_collected') 
                  // into 'Title Case' display keys (e.g., 'Total Amount Collected')
                  const displayKey = key.split('_')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(' ');

                  // Assign the value from the metadata object
                  acc[displayKey] = metadata[key];
                  return acc;
              }, {}); // Initialize the accumulator as an empty object
              return {
                // ... (Other properties remain the same)
                id: isForwardTracking ? level._id : level.previous_transaction_id,
                // label: level.label,
                
                label1: (level.labels && level.labels[0]) || null, 
                objectId: isForwardTracking ? level._id : level.previous_transaction_id,
                // amount: metadata.total_amount, 
                label2: (level.labels && level.labels[1]) || null, 
                hasChildren: isForwardTracking ? !!level._id : !!level.previous_transaction_id, 
                
                // Use the dynamically generated metadata object
                metadata: dynamicMetadata
              };
            });
        
        // return data.levels.map(level => ({
        //   // Use consistent ID for both id and objectId based on tracking direction
        //   id: isForwardTracking ? level._id : level.previous_transaction_id,
        //   label: level.txn_ref_no_bank ? level.txn_ref_no_bank : level.txn_ref_no,
        //   objectId: isForwardTracking ? level._id : level.previous_transaction_id,
        //   amount: level.total_amount_collected,
        //   // For forward tracking, always show expand if _id exists, for backward use previous_transaction_id
        //   hasChildren: isForwardTracking ? !!level._id : !!level.previous_transaction_id,
        //   metadata: {
        //     'Household': `${level.household_name} (${level.household_number})`,
        //     'Status': level.status,
        //     'Remark': level.remark
        //   }
        // }));
      } else if (data.status === 0 && data.message === 'NA') {
        // Return an artificial end node
        return [{
          id: 'end-node',
          label1: 'No further levels available',
          objectId: null,
          label2: '',
          hasChildren: false,
          metadata: {},
          isEndNode: true // special flag to style this node differently if needed
        }];
      }else if (data.status === 0 && data.message != "NA"){
        // Add an artificial node showing no further levels
        return [{
          id: 'end-node',
          label1: data.message,
          objectId: null,
          label2: '',
          hasChildren: false,
          metadata: {},
          isEndNode: true // special flag to style this node differently if needed
        }];
      } else {
        toast.error(data.message || 'Failed to load child nodes');
        return [];
      }
    } catch (error) {
      console.error("Error loading children:", error);
      toast.error("Failed to load child nodes");
      return [];
    }
  };

  return (
    <Modal 
      show={open} 
      onHide={() => onOpenChange(false)}
      centered
      scrollable
      dialogClassName={styles.customWideModal}
      contentClassName={styles.customModalContent}
    >
      <Modal.Header closeButton className={styles.customCloseHeader}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <Modal.Title className={styles.modalTitle}>{title}</Modal.Title>
            <p className="text-muted mb-0 mt-1 small">{description}</p>
          </div>
          <div className="form-check form-switch pr-4">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isForwardTracking}
              onChange={(e) => {
                const newValue = e.target.checked;  // true when switch is ON, false when OFF
                setIsForwardTracking(newValue);
                // Wait for state update using useEffect instead of calling loadRootNodes here
              }}
            />
            <label className="form-check-label ms-2" htmlFor="flexSwitchCheckDefault">
              Forward Tracking
            </label>
          </div>
        </div>
      </Modal.Header>
      
      <Modal.Body className={styles.modalBodyResponsive}>
        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Loader2 className={`${styles.spin} text-primary`} size={32} />
          </div>
        ) : rootNodes.length > 0 ? (
          <div className="d-flex flex-column gap-1">
            {rootNodes.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                onExpand={handleExpand}
                className={node.isEndNode ? 'text-muted fst-italic' : ''}
              />
            ))}
          </div>
        ) : null}
      </Modal.Body>
      
      
    </Modal>
  );
};
